import { Stack } from "@mui/material";
import { omit } from "lodash-es";
import type { FC } from "react";
import { prettyChannelName } from "../../../../channels/utils";
import { PreviewMissingButton } from "./components/preview-missing-button";
import { PreviewPersonsButton } from "./components/preview-persons-button";
import { PreviewTooltip } from "./components/preview-tooltip";
import { usePersonsAndMissing } from "./hooks/use-persons-and-missing";
import { PreviewDatagrid } from "./preview-datagrid";
import type { PreviewProps } from "./types";
import { subjectToChange } from "./utils";
import { useFlag } from "../../../../../server/optimizely";
import { useOptedOutPersons } from "./hooks/use-opted-out-persons";
import { PreviewOptedOutButton } from "./components/preview-opt-out-button";

export const ManagerPreview: FC<PreviewProps<"manager">> = ({ data, channel, ...rest }) => {
  const { persons, missing } = usePersonsAndMissing(channel, data);
  const optedOutPersons = useOptedOutPersons(channel, data);
  const [optOutsEnabled] = useFlag("opt_outs");
  const channelName = prettyChannelName(channel);
  const recipientLabel = persons.length === 1 ? "recipient" : "recipients";
  const managerLabel = data.length === 1 ? "Manager" : "Managers";
  const previewTitle =
    `${persons.length} ${channelName} ${recipientLabel} for ` +
    `${data.length} ${managerLabel} of people in the segment. ` +
    subjectToChange;
  const missingManagerLabel = missing.length === 1 ? "Manager" : "Managers";
  const missingVerb = missing.length === 1 ? "doesn't" : "don't";
  const missingClickableText = `${missing.length} ${missingManagerLabel} of people in this segment`;
  const missingSuffixText = `${missingVerb} have a ${channelName} connected.`;

  const optedOutPeopleLabel = optedOutPersons.length === 1 ? "person" : "people";
  const optedOutVerb = optedOutPersons.length === 1 ? "has" : "have";
  const optedOutClickableText = `${optedOutPersons.length} ${optedOutPeopleLabel} in this segment`;
  const optedOutSuffixText = `${optedOutVerb} opted out of ${channelName}`;

  return (
    <>
      <Stack direction="column" alignItems="flex-end" {...omit(rest, "preview_config")}>
        <Stack direction="row" spacing={2} alignItems="center">
          <PreviewPersonsButton
            persons={persons.length}
            channel={channel}
            buttonText="Manager Recipients: "
          >
            <PreviewDatagrid channelName={channel} persons={persons} connection="Manager" />
          </PreviewPersonsButton>

          <PreviewTooltip title={previewTitle} />
        </Stack>
        {missing.length > 0 && (
          <PreviewMissingButton
            clickableText={missingClickableText}
            suffixText={missingSuffixText}
            channelName={channel}
          >
            <PreviewDatagrid channelName={null} persons={missing} connection="Manager" />
          </PreviewMissingButton>
        )}
        {optOutsEnabled && optedOutPersons.length > 0 && (
          <PreviewOptedOutButton
            clickableText={optedOutClickableText}
            suffixText={optedOutSuffixText}
            channelName={channel}
          >
            <PreviewDatagrid channelName={channel} persons={optedOutPersons} />
          </PreviewOptedOutButton>
        )}
      </Stack>
    </>
  );
};
